import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from 'src/api/axiosClient/types';
import { RootState } from 'src/store';
import { Contact, ContactWithGroups, GetContactsParams } from '../types';
import { ContactsState } from './types';

const initialState: ContactsState = {
  loading: {
    getList: false,
    getListWithGroups: false,
    getListNotInGroup: false,
    delete: false,
    updateContactInfo: false,
    assignUser: false,
    changeContactGroups: false,
    changeContactMetadata: false,
    getContactGroups: false,
    optOutContact: false,
  },
  filter: {
    companyId: '',
    offset: 0,
    limit: 25,
    page: 0,
  },
  list: null,
  filterWithGroups: {
    companyId: '',
    offset: 0,
    limit: 25,
    page: 0,
  },
  listWithGroups: null,
  filterNotInGroup: {
    companyId: '',
    offset: 0,
    limit: 25,
    page: 0,
  },
  listNotInGroup: null,
  error: null,
};

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setLoading: (
      state,
      action: PayloadAction<Partial<Record<keyof ContactsState['loading'], boolean>>>,
    ) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    setFilter(state, action: PayloadAction<Partial<GetContactsParams>>) {
      state.filter = {
        ...state.filter,
        ...action.payload,
      };
    },
    setList: (
      state,
      action: PayloadAction<{
        items: Contact[];
        count: number;
      }>,
    ) => {
      state.list = action.payload;
    },
    setFilterWithGroups(state, action: PayloadAction<Partial<GetContactsParams>>) {
      state.filterWithGroups = {
        ...state.filterWithGroups,
        ...action.payload,
      };
    },
    setListWithGroups: (
      state,
      action: PayloadAction<{
        items: ContactWithGroups[];
        count: number;
      }>,
    ) => {
      state.listWithGroups = action.payload;
    },
    setFilterNotInGroup(state, action: PayloadAction<Partial<GetContactsParams>>) {
      state.filterNotInGroup = {
        ...state.filterNotInGroup,
        ...action.payload,
      };
    },
    setListNotInGroup: (
      state,
      action: PayloadAction<{
        items: Contact[];
        count: number;
      }>,
    ) => {
      state.listNotInGroup = action.payload;
    },
    setError: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
    },
    resetList: state => {
      state.filter = initialState.filter;
      state.list = initialState.list;
    },
    resetListWithGroups: state => {
      state.filterWithGroups = initialState.filterWithGroups;
      state.listWithGroups = initialState.listWithGroups;
    },
    resetListNotInGroup: state => {
      state.filterNotInGroup = initialState.filterNotInGroup;
      state.listNotInGroup = initialState.listNotInGroup;
    },
    resetError: state => {
      state.error = initialState.error;
    },
    resetSlice: () => {
      initialState;
    },
  },
});

export const isContactsLoading = (state: RootState) => state.contacts.loading;

export const getContacts = (state: RootState) => state.contacts.list;

export const getContactsWithGroups = (state: RootState) => state.contacts.listWithGroups;

export const getContactsNotInGroup = (state: RootState) => state.contacts.listNotInGroup;

export const isContactsError = (state: RootState) => state.contacts.error;

export const {
  setLoading,
  setFilter,
  setList,
  setFilterWithGroups,
  setListWithGroups,
  setFilterNotInGroup,
  setListNotInGroup,
  setError,
  resetList,
  resetListWithGroups,
  resetListNotInGroup,
  resetError,
  resetSlice,
} = contactsSlice.actions;

export const contactsReducer = contactsSlice.reducer;
